import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ref, getDownloadURL } from "firebase/storage";
import { Button } from "@/components/ui/button";
import { X, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { VoiceTextInput } from "@/components/VoiceTextInput";
import type { ContentBlock, StorageImageBlock, Base64ContentBlock } from "../types/messages";
import { useStorageContext } from "@/contexts/use-storage";
import { getFirebaseStorage } from "@/lib/firebase";

interface ImageDescriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  block: ContentBlock | null;
  onSave: (description: string, descriptionAudioUrls?: string[]) => void;
}

// Type guard for storage image blocks
function isStorageImageBlock(block: ContentBlock): block is StorageImageBlock {
  return block.type === "image" && block.source_type === "storage";
}

// Type guard for base64 image blocks
function isBase64ImageBlock(block: ContentBlock): block is Base64ContentBlock {
  return block.type === "image" && block.source_type === "base64";
}

export const ImageDescriptionDialog: React.FC<ImageDescriptionDialogProps> = ({
  open,
  onOpenChange,
  block,
  onSave,
}) => {
  const { userId, conversationId } = useStorageContext();
  const { t } = useTranslation("chat");
  const [description, setDescription] = useState("");
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [audioUrls, setAudioUrls] = useState<string[]>([]);
  const [hasActiveProcess, setHasActiveProcess] = useState(false);
  const [authenticatedImageUrl, setAuthenticatedImageUrl] = useState<string | null>(null);
  const [_isLoadingUrl, setIsLoadingUrl] = useState(false);

  // Initialize description and audio URLs from block metadata when dialog opens
  useEffect(() => {
    if (open && block) {
      if (isStorageImageBlock(block)) {
        setDescription(block.metadata?.description || "");
        setAudioUrls(block.metadata?.descriptionAudioUrls || []);
      } else if (isBase64ImageBlock(block)) {
        setDescription(block.metadata?.description || "");
        setAudioUrls(block.metadata?.descriptionAudioUrls || []);
      }
    }
  }, [open, block]);

  // Fetch authenticated Firebase Storage URL when dialog opens for storage images
  useEffect(() => {
    if (!open || !block || !isStorageImageBlock(block)) {
      setAuthenticatedImageUrl(null);
      return;
    }

    // Need userId and conversationId to build the storage path
    if (!userId || !conversationId || !block.fileName) {
      setAuthenticatedImageUrl(null);
      return;
    }

    const fetchAuthenticatedUrl = async () => {
      setIsLoadingUrl(true);
      try {
        const storage = getFirebaseStorage();
        const storagePath = `users/${userId}/projects/${conversationId}/images/${block.fileName}`;
        const storageRef = ref(storage, storagePath);
        const url = await getDownloadURL(storageRef);
        setAuthenticatedImageUrl(url);
      } catch (error) {
        console.error("[ImageDescriptionDialog] Failed to get authenticated URL:", error);
        // Fallback handled by getImageUrl using thumbnail
        setAuthenticatedImageUrl(null);
      } finally {
        setIsLoadingUrl(false);
      }
    };

    fetchAuthenticatedUrl();
  }, [open, block, userId, conversationId]);

  // Get thumbnail URL for blur placeholder (memoized)
  const thumbnailUrl = useMemo((): string | null => {
    if (!block) return null;

    if (isStorageImageBlock(block)) {
      return block.metadata?.thumbnailUrl || null;
    }

    if (isBase64ImageBlock(block)) {
      return block.thumbnail ? `data:${block.mime_type};base64,${block.thumbnail}` : null;
    }

    return null;
  }, [block]);

  // Get full quality image URL (memoized to avoid recreating object URLs)
  // For storage images, we use Firebase SDK's getDownloadURL which returns an authenticated URL.
  // Falls back to thumbnail (base64) while loading or if auth fails.
  const imageUrl = useMemo((): string | null => {
    if (!block) return null;

    if (isStorageImageBlock(block)) {
      // Use authenticated URL from Firebase SDK if available (full quality)
      if (authenticatedImageUrl) {
        return authenticatedImageUrl;
      }
      // Fallback to thumbnail while loading or if auth failed
      if (block.thumbnail) {
        return `data:image/jpeg;base64,${block.thumbnail}`;
      }
      return null;
    }

    if (isBase64ImageBlock(block)) {
      // Check if block is still processing (instant preview feature)
      const isProcessing = (block as any).processing === true;
      const file = (block as any).file as File | undefined;

      // If processing, use object URL from File if available
      if (isProcessing && file) {
        return URL.createObjectURL(file);
      }

      // For base64 blocks with data, prefer full data over thumbnail for better quality
      if (block.data) {
        return `data:${block.mime_type};base64,${block.data}`;
      }

      // Fallback to thumbnail if no data yet (during processing)
      if (block.thumbnail) {
        return `data:${block.mime_type};base64,${block.thumbnail}`;
      }
    }

    return null;
  }, [block, authenticatedImageUrl]);

  // Reset image loaded state when dialog opens or image URL changes
  useEffect(() => {
    if (open) {
      setIsImageLoaded(false);
    }
  }, [open, imageUrl]);

  // Cleanup object URL when image URL changes (for instant preview feature)
  useEffect(() => {
    return () => {
      // Revoke object URL if it was created from a File
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  const handleSave = () => {
    onSave(description, audioUrls.length > 0 ? audioUrls : undefined);
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:items-center"
          aria-describedby={undefined}
        >
          {/* Visually hidden title for accessibility */}
          <DialogPrimitive.Title className="sr-only">
            {t("multimodal.imageDescription.title")}
          </DialogPrimitive.Title>

          <div className="bg-background relative w-full sm:w-auto sm:min-w-[600px] h-[calc(100dvh-2rem)] sm:h-auto sm:min-h-[650px] sm:max-w-2xl border border-border shadow-lg overflow-hidden flex flex-col">
            {/* Content */}
            <div className="flex flex-col flex-1 min-h-0">
              {/* Image preview with blur-up lazy loading */}
              {imageUrl && (
                <div className="relative overflow-hidden bg-secondary flex-shrink-0">
                  <div className="aspect-[4/3] sm:aspect-[16/10] w-full max-h-[50dvh] sm:max-h-[400px] relative">
                    {/* Blurred thumbnail as placeholder */}
                    {thumbnailUrl && (
                      <img
                        src={thumbnailUrl}
                        alt=""
                        aria-hidden="true"
                        className={cn(
                          "absolute inset-0 h-full w-full object-contain scale-105 blur-md transition-opacity duration-300",
                          isImageLoaded ? "opacity-0" : "opacity-100"
                        )}
                      />
                    )}
                    {/* Full quality image */}
                    <img
                      src={imageUrl}
                      alt={t("multimodal.imageDescription.title")}
                      onLoad={() => setIsImageLoaded(true)}
                      onError={(e) => console.error("[ImageDescriptionDialog] Image failed to load:", e)}
                      className={cn(
                        "absolute inset-0 h-full w-full object-contain transition-opacity duration-300",
                        isImageLoaded ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </div>

                  {/* Close button overlay */}
                  <DialogPrimitive.Close asChild>
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="absolute top-2 right-2 rounded-full p-1.5 bg-black/50 text-white backdrop-blur-sm transition-colors hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-white/50"
                    >
                      <X className="h-4 w-4" />
                      <span className="sr-only">{t("multimodal.imageDescription.cancel")}</span>
                    </button>
                  </DialogPrimitive.Close>
                </div>
              )}

              {/* Voice text input for adding description */}
              <VoiceTextInput
                layout="horizontal"
                value={description}
                onChange={setDescription}
                placeholder={t("multimodal.imageDescription.placeholder")}
                enableVoice={true}
                initialAudioUrls={audioUrls}
                onAudioUrlsChange={setAudioUrls}
                onActiveProcessChange={setHasActiveProcess}
                maxRecordingDuration={60}
                autoFocus
                className="flex-1 min-h-0"
                renderActions={() => (
                  <Button
                    type="button"
                    size="icon"
                    onClick={handleSave}
                    disabled={hasActiveProcess}
                    className="rounded-none shadow-md transition-all"
                  >
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                )}
              />
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
